/**
 * Cuánto ocupa cada campo de la barra de búsqueda EN MOBILE.
 *
 * En el celular la fila mide poco más de 300 px y un control de fecha dibuja
 * su contenido en ~110: pedirle a la fecha el renglón entero deja dos tercios
 * vacíos Y empuja la hora al renglón siguiente. Por eso los campos cortos
 * declaran qué FRACCIÓN del renglón les toca (`xsShare`) y se emparejan.
 *
 * La fracción NO se puede usar como ancho tal cual: entre campo y campo hay un
 * `gap`, así que 60% + 40% mide un renglón MÁS el gap, no entra, y flexbox
 * manda el segundo campo abajo — el par queda declarado pero nunca ocurre. La
 * base de cada campo descuenta el gap ENTERO (sobra un poco, nunca falta, y
 * vale para cualquier cantidad de campos por renglón) y `flexGrow` reparte lo
 * que sobró: el par termina en 60/40 exactos, y un campo que quedó solo en su
 * renglón lo llena, que es como se ven hoy los campos sin `xsShare`.
 */

/** El `gap` de la fila de campos de `SearchBar` (`theme.spacing(1)`). */
export const SEARCH_FIELD_GAP_PX = 8;

/** El campo largo de un par: la fecha, las edades de los menores. */
export const SEARCH_FIELD_WIDE_SHARE = 0.6;

/** El campo corto de un par: la hora, la cantidad de pasajeros. */
export const SEARCH_FIELD_NARROW_SHARE = 0.4;

/** Par parejo: ida y vuelta, residencia y forma de pago, latitud y longitud. */
export const SEARCH_FIELD_HALF_SHARE = 0.5;

/** Un campo sin par: ocupa el renglón entero, como antes de que hubiera pares. */
export const SEARCH_FIELD_FULL_SHARE = 1;

/** Piso legible de un campo largo que puede crecer y achicarse en desktop. */
export const SEARCH_FIELD_DESKTOP_GROW_MIN_PX = 180;

/** Espacio real del DatePicker localizado, incluido su botón de calendario. */
export const SEARCH_DATE_FIELD_WIDTH = 176;

/** El resumen de composición no cambia de ancho según el espacio sobrante. */
export const SEARCH_PASSENGER_FIELD_WIDTH = 220;

function normalizeShare(share: number): number {
  if (!Number.isFinite(share) || share <= 0) return SEARCH_FIELD_FULL_SHARE;
  return Math.min(share, SEARCH_FIELD_FULL_SHARE);
}

/**
 * El `flex-basis` en mobile de un campo que pide `share` del renglón. Se
 * descuenta el gap acá, una sola vez, para que ningún formulario tenga que
 * acordarse de hacerlo (y para que ninguno lo haga distinto).
 */
export function mobileFieldBasis(share: number = SEARCH_FIELD_FULL_SHARE): string {
  const percentage = normalizeShare(share) * 100;
  // Sin decimales de más: 0.6 * 100 en punto flotante no es 60 exacto.
  const rounded = Number(percentage.toFixed(4));
  return `calc(${rounded}% - ${SEARCH_FIELD_GAP_PX}px)`;
}

/**
 * El ancho mínimo de desktop evita que flexbox resuelva la falta de espacio
 * aplastando los controles. Los campos de ancho fijo conservan su medida; los
 * largos (`grow`) pueden ceder hasta un piso legible y después saltan de fila.
 */
export function desktopFieldMinWidth(
  width: number | string | undefined,
  grow: boolean,
): number | string {
  if (typeof width === 'number') {
    return grow ? Math.min(width, SEARCH_FIELD_DESKTOP_GROW_MIN_PX) : width;
  }
  if (width !== undefined) return width;
  return grow ? SEARCH_FIELD_DESKTOP_GROW_MIN_PX : 0;
}

/**
 * Los renglones que arma flexbox con esas fracciones, en un ancho de fila
 * dado. Es el mismo cálculo que hace el navegador —base de cada campo más el
 * gap contra el ancho disponible— y existe para poder AFIRMAR en un test que
 * la fecha y la hora comparten renglón, en vez de confiar en que el CSS haga
 * lo que dice el comentario.
 */
export function mobileFieldRows(shares: number[], rowWidthPx: number): number[][] {
  const rows: number[][] = [];
  let current: number[] = [];
  let used = 0;

  shares.forEach((share) => {
    const basis = Math.max(0, normalizeShare(share) * rowWidthPx - SEARCH_FIELD_GAP_PX);
    const needed = current.length ? SEARCH_FIELD_GAP_PX + basis : basis;

    if (current.length && used + needed > rowWidthPx) {
      rows.push(current);
      current = [share];
      used = basis;
      return;
    }

    current.push(share);
    used += needed;
  });

  if (current.length) rows.push(current);
  return rows;
}
